import type { EnemyArchId, MapDef, SpawnGroup, WaveDef } from './types';

/**
 * Waves past the authored list on an endless map. Pure function of the wave number, integer math
 * only, so client and server agree. Five patterns cycle, counts grow, HP compounds.
 */
export function waveFor(map: MapDef, n: number): WaveDef | undefined {
  const authored = map.waves[n - 1];
  if (authored) return authored;
  if (!map.endless) return undefined;
  const k = n - map.waves.length; // 1-based index among generated waves
  let hp = map.endless.startHpPct;
  for (let i = 1; i < k; i++) hp = Math.floor((hp * map.endless.hpGrowthPct) / 100);
  const grow = Math.floor((k - 1) / 2);
  const g = (arch: EnemyArchId, count: number, startTick: number, spacing: number): SpawnGroup => ({
    arch,
    count: Math.max(0, Math.min(40, count)),
    startTick,
    spacing: Math.max(4, spacing),
    hpPct: hp,
  });
  switch (k % 5) {
    case 1:
      return { spawns: [g('walker', 10 + grow, 0, 12 - Math.floor(grow / 3)), g('runner', 6 + grow, 40, 8)] };
    case 2:
      // Heavy wave: brutes up front, armour behind them from the second cycle on.
      return { spawns: [g('brute', 3 + Math.floor(grow / 2), 0, 45), g('walker', 10 + grow, 10, 10), g('armored', Math.floor(grow / 2), 60, 40)] };
    case 3:
      // Fast wave: a runner swarm with flyers over the top.
      return { spawns: [g('runner', 14 + grow * 2, 0, 6), g('flyer', 3 + grow, 30, 10), g('brute', 2 + Math.floor(grow / 3), 60, 40)] };
    case 4:
      return { spawns: [g('walker', 14 + grow, 0, 9), g('brute', 4 + Math.floor(grow / 2), 30, 35), g('runner', 8 + grow, 80, 7), g('flyer', 2 + Math.floor(grow / 2), 100, 12)] };
    default:
      // Every fifth generated wave: a boss with an escort.
      return { spawns: [g('walker', 8 + grow, 0, 10), g('armored', 2 + Math.floor(grow / 2), 30, 40), g('boss', 1 + Math.floor(k / 15), 50, 120)] };
  }
}

/** True when a wave exists at this number: always on an endless map. */
export function hasWave(map: MapDef, n: number): boolean {
  return waveFor(map, n) !== undefined;
}
